import { useQuery } from '@tanstack/react-query';
import { DriveSweepModel } from '../../../../types';
import { useMemo, useState } from 'react';
import { UserKeys, getAllDriveMonitors } from '../../../api/users-api';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import DeleteDriveSweep from './delete-drive-sweep-dialog';
import EditDriveSweepDialog from './edit-drive-sweep/edit-drive-sweep-dialog';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import DriveSweepLogsDialog from './drive-sweep-logs-dialog';
import AvatarChip from '../../../shared/avatar-chip';
import DriveSweepToolbar from './drive-sweep-toolbar';
import DisableDriveSweepDialog from './disable-drive-sweep-dialog';
import EnableDriveSweepDialog from './enable-drive-sweep-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useDriveSweepActions } from './use-drive-sweep-actions';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import StatusLabel from '../../../shared/status-label';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import DriveSweepDetailsDrawer from './drive-sweep-details-drawer';
import DriveSweepDetailsDrawerOld from './drive-sweep-details-drawer-old';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import CleaningServices from '@mui/icons-material/CleaningServices';
import Edit from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import List from '@mui/icons-material/List';
import PlayArrow from '@mui/icons-material/PlayArrow';
export default function DriveSweepGrid() {
  const { driveRoles, changeLogRoles, viewType, features } = useUserSettings();
  const uiEnhancementFeatureEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );
  const {
    handleStartDriveSweep,
    handleRunWithChangeLog,
    isStartingSweep,
    openChangeLogDialog,
    setOpenChangeLogDialog,
    changeLogMessage,
    setChangeLogMessage,
    setDriveSweepToStart,
  } = useDriveSweepActions();

  const [openDeleteDriveSweep, setOpenDeleteDriveSweep] = useState(false);
  const [openEditDriveSweep, setOpenEditDriveSweep] = useState(false);
  const [selectedDriveSweep, setSelectedDriveSweep] =
    useState<DriveSweepModel | null>(null);
  const [openEnableSweeps, setOpenEnableSweeps] = useState(false);
  const [openDisableSweeps, setOpenDisableSweeps] = useState(false);
  const [openDriveSweepDetails, setOpenDriveSweepDetails] = useState(false);
  const [openDriveSweepLogs, setOpenDriveSweepLogs] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const { createEvent } = useAnalytics();

  const {
    data: sweepData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.DriveSweepModels],
    queryFn: getAllDriveMonitors,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('drive sweep logs'),
    },
  });

  const columns: GridColDef<DriveSweepModel>[] = useMemo(
    () => [
      {
        field: 'created',
        headerName: 'Created Date',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'lastRun',
        headerName: 'Last Run Date',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1,
        renderCell: (params) => {
          return params.value ? (
            <AvatarChip email={params.value} type="User" />
          ) : (
            '-'
          );
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'desc',
        headerName: 'Description',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 110,
        display: 'flex',
        type: 'boolean',
        renderCell: (params) => {
          const status = params.row.isRunning
            ? 'INPROGRESS'
            : params.value
            ? 'ENABLED'
            : 'DISABLED';
          return <StatusLabel status={status} />;
        },
      },
      {
        field: 'folderName',
        headerName: 'Folder',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'ownerId',
        headerName: 'Owner',
        editable: false,
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];

          if (
            isAbleToSeeUIElement(
              driveRoles || [],
              DriveRoles.ViewDriveSweep.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                component={Link}
                label="Link"
                icon={
                  <Tooltip arrow title="Link">
                    <LinkIcon />
                  </Tooltip>
                }
                onClick={() => {
                  window.open(
                    `https://drive.google.com/drive/u/1/folders/${params.row.folderId}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              driveRoles || [],
              DriveRoles.CanEditDriveSweep.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Run"
                icon={
                  <Tooltip arrow title="Run Sweep">
                    <PlayArrow />
                  </Tooltip>
                }
                onClick={() => {
                  handleStartDriveSweep(params.row, changeLogRoles);
                }}
                showInMenu
              />
            );
            buttonArray.push(
              <GridActionsCellItem
                label="Edit"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedDriveSweep(params.row);
                  setOpenEditDriveSweep(true);
                }}
                showInMenu
              />
            );
          }

          buttonArray.push(
            <GridActionsCellItem
              label="Logs"
              icon={
                <Tooltip arrow title="View Logs">
                  <List />
                </Tooltip>
              }
              onClick={() => {
                setSelectedDriveSweep(params.row);
                setOpenDriveSweepLogs(true);
              }}
              showInMenu
            />
          );

          return buttonArray;
        },
      },
    ],
    [
      driveRoles,
      changeLogRoles,
      handleStartDriveSweep,
      setSelectedDriveSweep,
      setOpenEditDriveSweep,
      setOpenDriveSweepLogs,
    ]
  );

  const selectedDriveSweeps = useMemo(() => {
    return (
      sweepData?.filter((sweep) => selectedRows.includes(sweep.folderId)) || []
    );
  }, [sweepData, selectedRows]);

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'created',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      driveRoles,
      selectedDriveSweeps,
      setOpenDisableSweeps,
      setOpenEnableSweeps,
      setOpenDeleteDriveSweep,
    }),
    [driveRoles, selectedDriveSweeps]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.driveSweepsGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(DriveSweepToolbar, toolbarProps),
          ...createGridOverlays('Drive Sweeps', <CleaningServices />),
        }}
        checkboxSelection
        disableRowSelectionOnClick
        getRowId={(row) => row.folderId}
        rows={sweepData || []}
        columns={columns}
        sx={{
          mt: 1,
          minHeight: 600,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        loading={isLoading || isFetching}
        gridStateKey="driveSweepsGridStates"
        defaultGridState={defaultGridState}
        onRowClick={(row) => {
          setSelectedDriveSweep(row.row);
          setOpenDriveSweepDetails(true);
          createEvent(analytics.drive.drive_sweep.viewed_drive_sweep_details, {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveSweep,
          });
        }}
      />
      {selectedRows.length > 0 && openDeleteDriveSweep ? (
        <DeleteDriveSweep
          open={openDeleteDriveSweep}
          handleClose={() => setOpenDeleteDriveSweep(false)}
          selectedDriveSweeps={selectedDriveSweeps}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      {selectedDriveSweep && openEditDriveSweep ? (
        <EditDriveSweepDialog
          open={openEditDriveSweep}
          handleClose={() => setOpenEditDriveSweep(false)}
          selectedDriveSweep={selectedDriveSweep}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      {uiEnhancementFeatureEnabled ? (
        <DriveSweepDetailsDrawer
          open={openDriveSweepDetails}
          handleClose={() => setOpenDriveSweepDetails(false)}
          selectedDriveSweep={selectedDriveSweep}
        />
      ) : (
        <DriveSweepDetailsDrawerOld
          open={openDriveSweepDetails}
          handleClose={() => setOpenDriveSweepDetails(false)}
          selectedDriveSweep={selectedDriveSweep}
        />
      )}

      {selectedDriveSweep && openDriveSweepLogs ? (
        <DriveSweepLogsDialog
          open={openDriveSweepLogs}
          handleClose={() => setOpenDriveSweepLogs(false)}
          selectedDriveSweep={selectedDriveSweep}
        />
      ) : null}

      {selectedRows.length > 0 && openDisableSweeps ? (
        <DisableDriveSweepDialog
          open={openDisableSweeps}
          handleClose={() => setOpenDisableSweeps(false)}
          selectedDriveSweeps={selectedDriveSweeps}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      {selectedRows.length > 0 && openEnableSweeps ? (
        <EnableDriveSweepDialog
          open={openEnableSweeps}
          handleClose={() => setOpenEnableSweeps(false)}
          selectedDriveSweeps={selectedDriveSweeps}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setDriveSweepToStart(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={handleRunWithChangeLog}
        mutateLoading={isStartingSweep}
      />
    </>
  );
}
